import {
  ContentsResponse,
  deleteContent,
  getContent,
  GetContentOptions,
  getContents,
  getContentSlugsByIds,
  getContentSlugsByTypes,
  getSearchResult,
  BaseApiRequest,
  GetSearchResultOptions,
  ContentSearchResponse,
  SaveContentOperationMode,
  saveContent,
  deleteAllReverseRelations,
  deleteReverseRelation,
  getContentsMixed,
  GetContentsOptions,
  GetContentsSource,
  GetMixedContentsOptions,
  getReverseRelations,
  MixedContentsResponse,
  ContentSlug,
} from "@robe/core-api";
import { Content, ContentSlugsObject, ReverseRelation } from "@robe/core-types";
import { XcoreCore } from ".";

export class XcoreContent {
  private core: XcoreCore;

  public constructor(core: XcoreCore) {
    this.core = core;
  }

  /**
   * Fetches content that can be `singleton` or `multiple` by ID.
   *
   * @param id The ID of the content to fetch.
   * @param options (Optional),
   * an object containing options for fetching content, such as sorting, and filtering.
   * @param options.locales (Optional), content locales,
   * if provided locales have fallback content locale in config it will be included in request too.
   * @param options.publishedOnly (Optional), in web only published contents are being fetched by default
   * if not specified otherwise. In admin all contents are being fetched by default if not specified otherwise.
   * @param options.includeValues (Optional), include all content values in `values` field.
   * @param options.includeCategories (Optional), include categories in content.
   * Localized sub categories names are filled with `en` value if value is not available for some locale in sub categories, only on WEB.
   * @param options.fields (Optional), list of specific content fields to fetch.
   * `includeValues=true` param is automatically being included in request if `fields` param is specified.
   *
   * @returns An `BaseApiRequest` that resolves to a `ContentResponse<T>` or `null` if the content is not found.
   */
  public singleById<T extends Content>(id: number, options?: GetContentOptions<T>): BaseApiRequest<T | null> {
    return getContent<T>(id, this.core, options);
  }

  /**
   * Fetches `singleton` content by type.
   *
   * @param type The type of the content to fetch.
   * @param options (Optional),
   * an object containing options for fetching content, such as sorting, and filtering.
   * @param options.locales (Optional), content locales,
   * if provided locales have fallback content locale in config it will be included in request too.
   * @param options.publishedOnly (Optional), in web only published contents are being fetched by default
   * if not specified otherwise. In admin all contents are being fetched by default if not specified otherwise.
   * @param options.includeValues (Optional), include all content values in `values` field.
   * @param options.includeCategories (Optional), include categories in content.
   * Localized sub categories names are filled with `en` value if value is not available for some locale in sub categories, only on WEB.
   * @param options.fields (Optional), list of specific content fields to fetch.
   * `includeValues=true` param is automatically being included in request if `fields` param is specified.
   *
   * @returns An `BaseApiRequest` that resolves to a `ContentResponse<T>` or `null` if the content is not found.
   */
  public singletonByType<T extends Content>(type: string, options?: GetContentOptions<T>): BaseApiRequest<T | null> {
    return getContent<T>(type, this.core, options);
  }

  /**
   * Fetches `multiple` contents that correspond to specified type.
   *
   * @typeParam T - A type extending `Content`
   * @typeParam TField - A type representing the values field of `T` that should be included in the response.
   *
   * @param type The type of content that will be fetched.
   * @param options (Optional),
   * an object containing options for fetching content, such as sorting, and filtering.
   * @param options.locales (Optional), content locales,
   * if provided locales have fallback content locale in config it will be included in request too.
   * @param options.publishedOnly (Optional), in web only published contents are being fetched by default
   * if not specified otherwise. In admin all contents are being fetched by default if not specified otherwise.
   * @param options.sort (Optional),
   * content property or content field which the result should be sorted by.
   * Can be a `string`, a queryable object, or an array of these with a sort direction (`"desc"` or `"asc"`).
   * @param options.page (Optional), pagination page number.
   * @param options.perPage (Optional), how many results should be included in a page.
   * @param options.categories (Optional), Filter content by sub categories.
   * @param options.includeCategories (Optional), include categories in the contents.
   * Localized sub categories names are filled with `en` value if value is not available for some locale in sub categories, only on WEB.
   * @param options.createdAt (Optional), filter content by the date it was createdAt.
   * Can be an array of single string, that way it is considered as olderThan, or an array of two strings than it will be considered as olderThan and newerThan.
   * @param options.publishedAt (Optional), filter content by the date it was publishedAt. '
   * Can be an array of single string, that way it is considered as olderThan, or an array of two strings than it will be considered as olderThan and newerThan.
   * @param options.fields (Optional), list of specific content fields to fetch.
   * `includeValues=true` param is automatically being included in request if `fields` param is specified.
   * @param options.includeValues (Optional), include all content values. If used, it takes precedence over specified fields.
   * @param options.includeNonLocalized (Optional), include non localized contents even if `locale` param is provided.
   * @param paginate (Optional), Whether the response should be paginated. Defaults to `true`.
   * @param relations (Optional), Filter content by related content IDs.
   *
   *
   * @returns An `BaseApiRequest` that resolves to a `ContentsResponse<T, TField>`, containing the requested content with the specified fields.
   *
   * @example Fetching multiple contents by type.
   * ```
   * const data = useXcoreQuery((core) => core.content.multipleByType<News>("news", {
   *   fields: ["name", "image"],
   *   sort: { fieldName: "createdAt", orderBy "Descending" },
   *   perPage: 10,
   *   // ...other options
   * }));
   * ```
   */
  public multipleByType<T extends Content, TField extends keyof T["values"] & string = keyof T["values"] & string>(
    type: T["type"],
    options?: GetContentsOptions<T> & { fields?: TField[] }
  ): BaseApiRequest<ContentsResponse<T, TField>> {
    return getContents<T, TField>({ types: type }, this.core, options?.fields, options);
  }

  /**
   * Fetches `multiple` contents of same content type by IDs.
   *
   * @typeParam T - A type extending `Content`
   * @typeParam TField - A type representing the values field of `T` that should be included in the response.
   *
   * @param ids The IDs of contents that will be fetched.
   * @param options (Optional),
   * an object containing options for fetching content, such as sorting, and filtering.
   * @param options.locales (Optional), content locales,
   * if provided locales have fallback content locale in config it will be included in request too.
   * @param options.publishedOnly (Optional), in web only published contents are being fetched by default
   * if not specified otherwise. In admin all contents are being fetched by default if not specified otherwise.
   * @param options.sort (Optional),
   * content property or content field which the result should be sorted by.
   * Can be a `string`, a queryable object, or an array of these with a sort direction (`"desc"` or `"asc"`).
   * @param options.page (Optional), pagination page number.
   * @param options.perPage (Optional), how many results should be included in a page.
   * @param options.categories (Optional), Filter content by sub categories.
   * @param options.includeCategories (Optional), include categories in the contents.
   * Localized sub categories names are filled with `en` value if value is not available for some locale in sub categories, only on WEB.
   * @param options.createdAt (Optional), filter content by the date it was createdAt.
   * Can be an array of single string, that way it is considered as olderThan, or an array of two strings than it will be considered as olderThan and newerThan.
   * @param options.publishedAt (Optional), filter content by the date it was publishedAt. '
   * Can be an array of single string, that way it is considered as olderThan, or an array of two strings than it will be considered as olderThan and newerThan.
   * @param options.fields (Optional), list of specific content fields to fetch.
   * `includeValues=true` param is automatically being included in request if `fields` param is specified.
   * @param options.includeValues (Optional), include all content values. If used, it takes precedence over specified fields.
   * @param options.includeNonLocalized (Optional), include non localized contents even if `locale` param is provided.
   * @param paginate (Optional), Whether the response should be paginated. Defaults to `true`.
   *
   *
   * @returns An `BaseApiRequest` that resolves to a `ContentsResponse<T, TField>`, containing the requested content with the specified fields.
   *
   * @example Fetching contents of same content type by IDs.
   * ```
   * const data = useXcoreQuery((core) => core.content.multipleByIds<News>([4257, 4587], {
   *   fields: ["name", "image"],
   *   sort: { fieldName: "createdAt", orderBy "Descending" },
   *   perPage: 10,
   *   // ...other options
   * }));
   * ```
   */
  public multipleByIds<T extends Content, TField extends keyof T["values"] & string = keyof T["values"] & string>(
    ids: number[],
    options?: GetContentsSource<T> & GetContentsOptions<T> & { fields?: TField[] }
  ): BaseApiRequest<ContentsResponse<T, TField>> {
    return getContents<T, TField>({ ids }, this.core, options?.fields, options);
  }

  /**
   * Fetches `multiple` and `singleton` contents by content types.
   *
   * @typeParam T - A type extending `Content`
   * @typeParam TField - A type representing the values field of `T` that should be included in the response.
   *
   * @param ids The IDs of contents that will be fetched.
   * @param options (Optional),
   * an object containing options for fetching content, such as sorting, and filtering.
   * @param options.locales (Optional), content locales,
   * if provided locales have fallback content locale in config it will be included in request too.
   * @param options.publishedOnly (Optional), in web only published contents are being fetched by default
   * if not specified otherwise. In admin all contents are being fetched by default if not specified otherwise.
   * @param options.sort (Optional),
   * content property or content field which the result should be sorted by.
   * Can be a `string`, a queryable object, or an array of these with a sort direction (`"desc"` or `"asc"`).
   * @param options.page (Optional), pagination page number.
   * @param options.perPage (Optional), how many results should be included in a page.
   * @param options.categories (Optional), Filter content by sub categories.
   * @param options.includeCategories (Optional), include categories in the contents.
   * Localized sub categories names are filled with `en` value if value is not available for some locale in sub categories, only on WEB.
   * @param options.createdAt (Optional), filter content by the date it was createdAt.
   * Can be an array of single string, that way it is considered as olderThan, or an array of two strings than it will be considered as olderThan and newerThan.
   * @param options.publishedAt (Optional), filter content by the date it was publishedAt. '
   * Can be an array of single string, that way it is considered as olderThan, or an array of two strings than it will be considered as olderThan and newerThan.
   * @param options.fields (Optional), list of specific content fields to fetch.
   * `includeValues=true` param is automatically being included in request if `fields` param is specified.
   * @param options.includeValues (Optional), include all content values. If used, it takes precedence over specified fields.
   * @param options.includeNonLocalized (Optional), include non localized contents even if `locale` param is provided.
   * @param paginate (Optional), Whether the response should be paginated. Defaults to `true`.
   *
   *
   * @returns An `BaseApiRequest` that resolves to a `ContentsResponse<T, TField>`, containing the requested content with the specified fields.
   *
   * @example Fetching contents of same content type by IDs.
   * ```
   * const data = useXcoreQuery((core) => core.content.listByTypes<News, Products>(["news", "products"], {
   *   fields: ["name", "image"],
   *   sort: { fieldName: "createdAt", orderBy "Descending" },
   *   perPage: 10,
   *   // ...other options
   * }));
   * ```
   */
  public listByTypes<T extends Content, TFields extends object = object>(
    types: string[],
    options?: GetMixedContentsOptions<TFields>
  ): BaseApiRequest<MixedContentsResponse<TFields, T>> {
    return getContentsMixed<T, TFields>({ types }, this.core, options?.fields, options);
  }

  /**
   * Fetches contents that can be `singleton` or `multiple` by IDs.
   * Use this only if IDs are of different content types, if not prefer using `multipleByIds()` or `singleById()` instead to keep type safety.
   *
   * @typeParam T - A type extending `Content`
   * @typeParam TField - A type representing the values field of `T` that should be included in the response.
   *
   * @param source IDs or types of contents that will be fetched.
   * @param source.ids The IDs of contents that will be fetched.
   * @param source.types The types of contents that will be fetched.
   * @param options (Optional),
   * an object containing options for fetching content, such as sorting, and filtering.
   * @param options.locales (Optional), content locales,
   * if provided locales have fallback content locale in config it will be included in request too.
   * @param options.publishedOnly (Optional), in web only published contents are being fetched by default
   * if not specified otherwise. In admin all contents are being fetched by default if not specified otherwise.
   * @param options.sort (Optional),
   * content property or content field which the result should be sorted by.
   * Can be a `string`, a queryable object, or an array of these with a sort direction (`"desc"` or `"asc"`).
   * @param options.page (Optional), pagination page number.
   * @param options.perPage (Optional), how many results should be included in a page.
   * @param options.categories (Optional), Filter content by sub categories.
   * @param options.includeCategories (Optional), include categories in the contents.
   * Localized sub categories names are filled with `en` value if value is not available for some locale in sub categories, only on WEB.
   * @param options.createdAt (Optional), filter content by the date it was createdAt.
   * Can be an array of single string, that way it is considered as olderThan, or an array of two strings than it will be considered as olderThan and newerThan.
   * @param options.publishedAt (Optional), filter content by the date it was publishedAt. '
   * Can be an array of single string, that way it is considered as olderThan, or an array of two strings than it will be considered as olderThan and newerThan.
   * @param options.fields (Optional), list of specific content fields to fetch.
   * `includeValues=true` param is automatically being included in request if `fields` param is specified.
   * @param options.includeValues (Optional), include all content values. If used, it takes precedence over specified fields.
   * @param options.includeNonLocalized (Optional), include non localized contents even if `locale` param is provided.
   * @param paginate (Optional), Whether the response should be paginated. Defaults to `true`.
   *
   *
   * @returns An `BaseApiRequest` that resolves to a `ContentsResponse<T, TField>`, containing the requested content with the specified fields.
   *
   * @example Fetching contents of different types by IDs.
   * Result can be filtered for certain type and casted with created fields select.
   * Keep in mind that when using this for content types and specified field exists in more than one content type it will be fetched for both.
   * ```
   *  type NewsFieldsSelect = News<"intro" | "images">;
   *  type ProductFieldsSelect = Product<"intro" | "images" | "accessories">;
   *
   *  const data = useXcoreQuery((core) => core.content.listByIds<NewsFieldsSelect | ProductFieldsSelect, NewsValues & ProductValues>(
   *    [332, 598, 5260, 405],
   *    {
   *      fields: ["intro", "images", "accessories"],
   *      includeValues: true
   *    }
   *  ))
   * ```
   *
   */
  public listByIds<T extends Content, TFields extends object>(
    ids: number[],
    options?: GetMixedContentsOptions<TFields>
  ): BaseApiRequest<MixedContentsResponse<TFields, T>> {
    return getContentsMixed<T, TFields>({ ids }, this.core, options?.fields, options);
  }

  /**
   * Fetches all contents with default pagination.
   *
   * @typeParam T - A type extending `Content`
   * @typeParam TField - A type representing the values field of `T` that should be included in the response.
   *
   * @param options (Optional),
   * an object containing options for fetching content, such as sorting, and filtering.
   * @param options.locales (Optional), content locales,
   * if provided locales have fallback content locale in config it will be included in request too.
   * @param options.publishedOnly (Optional), in web only published contents are being fetched by default
   * if not specified otherwise. In admin all contents are being fetched by default if not specified otherwise.
   * @param options.sort (Optional),
   * content property or content field which the result should be sorted by.
   * Can be specified fieldName to sort by and order of the sorting as orderBy: "Descending" | "Ascending"
   * @param options.page (Optional), pagination page number.
   * @param options.perPage (Optional), how many results should be included in a page.
   * @param options.categories (Optional), Filter content by sub categories.
   * @param options.includeCategories (Optional), include categories in the contents.
   * Localized sub categories names are filled with `en` value if value is not available for some locale in sub categories, only on WEB.
   * @param options.createdAt (Optional), filter content by the date it was createdAt.
   * Can be an array of single string, that way it is considered as olderThan, or an array of two strings than it will be considered as olderThan and newerThan.
   * @param options.publishedAt (Optional), filter content by the date it was publishedAt. '
   * Can be an array of single string, that way it is considered as olderThan, or an array of two strings than it will be considered as olderThan and newerThan.
   * @param options.fields (Optional), list of specific content fields to fetch.
   * `includeValues=true` param is automatically being included in request if `fields` param is specified.
   * @param options.includeValues (Optional), include all content values. If used, it takes precedence over specified fields.
   * @param options.includeNonLocalized (Optional), include non localized contents even if `locale` param is provided.
   * @param paginate (Optional), Whether the response should be paginated. Defaults to `true`.
   *
   *
   * @returns An `BaseApiRequest` that resolves to a `ContentsResponse<T, TField>`, containing the requested content with the specified fields.
   *
   * @example Fetching all contents.
   * ```
   * const data = useXcoreQuery((core) => core.content.all({
   *   perPage: 20,
   *   // ...other options
   * }));
   * ```
   */
  public all<T extends Content, TField extends keyof T["values"] & string = keyof T["values"] & string>(
    options?: GetContentsOptions<T> & { fields?: TField[] }
  ): BaseApiRequest<ContentsResponse<T, TField>> {
    return getContents<T, TField>({}, this.core, options?.fields, options);
  }

  /**
   * Fetches content slugs based on the provided content types.
   *
   * @param contentTypes An array of content types for which slugs should be fetched.
   */
  public slugsByTypes(contentTypes: string[]): BaseApiRequest<ContentSlugsObject> {
    return getContentSlugsByTypes(this.core, contentTypes);
  }

  /**
   * Fetches content slugs based on the provided content types.
   *
   * @param ids An array of content ids for which slugs should be fetched.
   */
  public slugsByIds(ids: number[]): BaseApiRequest<Array<ContentSlug>> {
    return getContentSlugsByIds(this.core, ids);
  }

  /**
   * Fetches search results based on the provided search term, content types, and request context.
   * This function is designed to query the content search API and return a promise resolving to a partial record of content IDs grouped by their types.
   *
   * @typeParam TKey A type extending `string`, representing the keys of the result object, which are the content types.
   *
   * @param term The search term to query the content search API.
   * @param types (Optional), an array of content types to limit the search results to.
   * @param options.fields (Optional), list of specific content fields to fetch.
   * @param options.publishedOnly (Optional), in web only published contents are being fetched by default
   * if not specified otherwise. In admin all contents are being fetched by default if not specified otherwise.
   * @param options.locales (Optional), fetch only contents with locales. If no locales are specified all contents will be fetched.
   * @param options.includeValues (Optional), include all content values in `values` field.
   * Doesn't have any effect if `fields` param is being used.
   * @param options.includeContentData (Optional), include rest of content data.
   * This is controlled on EP by `minimal` param but is set to `false` by default on BE.
   * It is better to have `minimal` set to `true` in requests by default, and only fetch additional data if we really need it.
   * Doesn't have any effect if `fields` param is being used.
   * @param options.page (Optional), which page of total pages should be fetched.
   * @param options.perPage (Optional), number of items per page.
   * @param options.count (Optional), max number of contents that should be fetched in single page.
   * @param options.length (Optional), max number of total contents that should be fetched.
   *
   * @returns An `BaseApiRequest` that resolves to a `ContentSearchResponse`, where each key is a content type and the value is an array of contents.
   *
   */
  public search(term: string, types?: string[], options?: GetSearchResultOptions): BaseApiRequest<ContentSearchResponse> {
    return getSearchResult(this.core, term, types, options);
  }

  public update<T extends Content>(content: T): BaseApiRequest<Content> {
    return saveContent(SaveContentOperationMode.Update, this.core, content);
  }

  public insert<T extends Content>(content: T): BaseApiRequest<Content> {
    return saveContent(SaveContentOperationMode.Create, this.core, content);
  }

  public delete(contentId: number): BaseApiRequest<void> {
    return deleteContent(contentId, this.core);
  }

  public getReverseRelations(contentId: number): BaseApiRequest<Array<ReverseRelation>> {
    return getReverseRelations(contentId, this.core);
  }

  public deleteReverseRelation(reverseRelation: ReverseRelation, forceDelete?: boolean): BaseApiRequest<void> {
    return deleteReverseRelation(reverseRelation, this.core, forceDelete);
  }

  public deleteAllReverseRelations(content: number, forceDelete?: boolean): BaseApiRequest<void> {
    return deleteAllReverseRelations(content, this.core, forceDelete);
  }

  public empty(): Content {
    return {
      id: 0,
      isSingle: true,
      type: "",
      title: {
        value: "",
      },
      isDraft: true,
      slug: {
        value: null,
      },
      publishedAt: {
        value: null,
      },
      createdAt: "1970-01-01T00:00:00.000Z",
      updatedAt: "1970-01-01T00:00:00.000Z",
      subCategories: null,
      values: {},
      slugAliases: [],
      locales: [],
      imageInfos: [],
    };
  }
}
